import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import FileUpload from '@/components/extract/FileUpload';
import YouTubeInput from '@/components/extract/YouTubeInput';
import ExtractionSettings from '@/components/extract/ExtractionSettings';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Music, Play } from 'lucide-react';
import { musicExtractionApi } from '@/services/musicExtractionApi';
import LoaderPopup from '@/components/extract/LoaderPopup';
import { AnimatePresence, motion } from 'framer-motion';

export default function ExtractMusic() {
  const { user, profile, incrementUsage } = useAuth();
  const { toast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoaderVisible, setIsLoaderVisible] = useState(false);
  const [popupStatus, setPopupStatus] = useState<'loading' | 'queued' | 'error'>('loading');
  const [queueMeta, setQueueMeta] = useState<{ email?: string; priority?: string; jobId?: string }>({});
  const [settings, setSettings] = useState({
    minDuration: 180,
    padding: 30,
    tolerance: 120,
  });
  const [planLimits, setPlanLimits] = useState<{ api_calls_limit: number; monthly_space_limit: number } | null>(null);

  React.useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!profile) return;
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('api_calls_limit, monthly_space_limit')
        .eq('name', profile.subscription_plan)
        .single();
      if (!error && data) {
        setPlanLimits({
          api_calls_limit: data.api_calls_limit,
          monthly_space_limit: data.monthly_space_limit,
        });
      }
    };
    fetchPlanLimits();
  }, [profile]);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setYoutubeUrl('');
  };

  const handleYoutubeUrl = (url: string) => {
    setYoutubeUrl(url);
    setSelectedFile(null);
  };

  const handleSettingsChange = (key: string, value: number) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const startExtraction = async () => {
    if (!user || !profile || !planLimits) return;

    const isPaid = profile.subscription_plan !== 'free';

    // Check API and space limits from DB
    const planApiLimit = planLimits.api_calls_limit;
    const planSpaceLimit = planLimits.monthly_space_limit;
    const nextApiCalls = (profile.api_calls_used || 0) + 1;
    const nextSpaceUsed = (profile.monthly_space_used || 0) + (selectedFile?.size || 0);

    if (nextApiCalls > planApiLimit) {
      toast({
        title: "API Limit Exceeded",
        description: `You have reached your monthly API call limit (${planApiLimit}). Please wait until next month or upgrade your plan.`,
        variant: "destructive",
      });
      return;
    }
    if (selectedFile && nextSpaceUsed > planSpaceLimit) {
      toast({
        title: "Storage Limit Exceeded",
        description: `Uploading this file would exceed your monthly storage limit (${(planSpaceLimit/1024/1024/1024).toFixed(1)} GB). Please wait until next month or upgrade your plan.`,
        variant: "destructive",
      });
      return;
    }

    if (!selectedFile && !youtubeUrl) {
      toast({
        title: "No source selected",
        description: "Please select a file or enter a YouTube URL",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    setPopupStatus('loading');
    setIsLoaderVisible(true);

    try {
      // Create extraction record in database as pending (queued)
      const extractionData = {
        user_id: user.id,
        file_name: selectedFile?.name || 'YouTube Video',
        file_size: selectedFile?.size || 0,
        youtube_url: youtubeUrl || null,
        min_duration: settings.minDuration,
        padding: settings.padding,
        tolerance: settings.tolerance,
        status: 'pending' as const,
      };

      await supabase.from('video_extractions').insert(extractionData);

      toast({
        title: "Submitting your request",
        description: "We are adding your extraction to the processing queue...",
      });

      // Queue extraction
      let res:
        | { job_id: string; message: string; priority: string; status: string };

      if (selectedFile) {
        res = await musicExtractionApi.extractMusicDownload({
          file: selectedFile,
          emailId: user.email || '',
          isPaid,
          minDuration: settings.minDuration,
          tolerance: settings.tolerance,
          padding: settings.padding,
        });
      } else {
        res = await musicExtractionApi.extractMusicFromYouTube({
          url: youtubeUrl,
          emailId: user.email || '',
          isPaid,
          minDuration: settings.minDuration,
          tolerance: settings.tolerance,
          padding: settings.padding,
        });
      }

      // Immediately increment usage (API calls and storage used for uploads)
      await incrementUsage(1, selectedFile?.size || 0);

      // Show queued confirmation
      setPopupStatus('queued');
      setQueueMeta({
        email: user.email || undefined,
        priority: res.priority,
        jobId: res.job_id,
      });

      toast({
        title: isPaid ? 'High-Priority Request Queued' : 'Request Queued',
        description: isPaid
          ? 'Your request has been added to the high-priority queue and will be delivered soon via email.'
          : 'Your request has been added to the queue. We will email you the download link once processing is complete.',
      });

    } catch (error) {
      console.error('Error during extraction:', error);

      setPopupStatus('error');
      toast({
        title: "Failed to queue request",
        description: (error instanceof Error ? error.message : 'Please try again.'),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      // Keep popup open to show queued confirmation; user can close it.
    }
  };

  const canStartExtraction = (selectedFile || youtubeUrl) && !isProcessing;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-100">
      <div className="max-w-4xl mx-auto p-6 sm:p-8">
        <AnimatePresence>
          {isLoaderVisible && (
            <LoaderPopup
              onClose={() => setIsLoaderVisible(false)}
              status={popupStatus}
              email={queueMeta.email}
              priority={queueMeta.priority}
              jobId={queueMeta.jobId}
              onExtractAnother={() => {
                setIsLoaderVisible(false);
                setPopupStatus('loading');
                setSelectedFile(null);
                setYoutubeUrl('');
              }}
            />
          )}
        </AnimatePresence>
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="mb-10 text-center"
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            Extract Your Music
          </h1>
          <p className="text-lg text-gray-600">
            AI-powered music extraction from any video source.
          </p>
        </motion.div>


        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-5 gap-8"
        >
          <motion.div variants={itemVariants} className="lg:col-span-3 space-y-6">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-xl">
                  <Music className="h-6 w-6 text-purple-600" />
                  <span>1. Select Source</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="file" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="file">File Upload</TabsTrigger>
                    <TabsTrigger value="youtube">YouTube URL</TabsTrigger>
                  </TabsList>
                  <TabsContent value="file" className="mt-4">
                    <FileUpload
                      onFileSelect={handleFileSelect}
                      selectedFile={selectedFile}
                      onClearFile={() => setSelectedFile(null)}
                    />
                  </TabsContent>
                  <TabsContent value="youtube" className="mt-4">
                    <YouTubeInput
                      onUrlSubmit={handleYoutubeUrl}
                      disabled={isProcessing}
                    />
                    {youtubeUrl && (
                      <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-sm text-green-800">
                          <strong>YouTube URL loaded:</strong> {youtubeUrl}
                        </p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            <motion.div variants={itemVariants}>
              <ExtractionSettings
                settings={settings}
                onSettingsChange={handleSettingsChange}
              />
            </motion.div>
          </motion.div>


          <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
            <Card className="bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-2xl h-full flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-xl">
                  <Play className="h-6 w-6" />
                  <span>2. Start Extraction</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow flex flex-col items-center justify-center text-center p-8">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Music className="h-16 w-16 mx-auto mb-6 opacity-80" />
                </motion.div>
                <h3 className="text-2xl font-semibold mb-3">Ready to Go!</h3>
                <p className="opacity-90 mb-6">
                  Once your source is selected, you can start the extraction process.
                </p>
                <Button
                  onClick={startExtraction}
                  disabled={!canStartExtraction}
                  className="w-full bg-white text-purple-600 hover:bg-gray-100 transition-transform duration-200 hover:scale-105"
                  size="lg"
                >
                  <Play className="h-5 w-5 mr-2" />
                  {isProcessing ? 'Submitting...' : 'Start Extraction'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
